import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

export const RevenueCounter = () => {
  const [rev, setRev] = useState({ gross: 0, orders: 0, month_gross: 0, goal: 10000 });

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/revenue").then((r) => alive && setRev(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const goal = Number(rev.goal || 0);
  const month = Number(rev.month_gross || 0);
  const pct = goal > 0 ? Math.min(100, (month / goal) * 100) : 0;

  return (
    <section id="revenue" className="max-w-[1600px] mx-auto px-6 md:px-10 py-16">
      <div className="relative border border-border bg-card overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,46,46,0.14),transparent_60%)]" />
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative px-8 md:px-14 py-12 md:py-16 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-volt animate-pulse" /> Live · Studio Revenue
            </span>
            <motion.div
              key={rev.gross}
              initial={{ opacity: 0.4, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              data-testid="revenue-gross"
              className="font-display font-black text-6xl sm:text-7xl lg:text-8xl tracking-tighter mt-4 tabular-nums"
            >
              ${Number(rev.gross || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </motion.div>
            <p className="text-muted-foreground text-sm mt-3">Total earned through the site across card, CashApp &amp; Apple Pay — updating live.</p>
          </div>
          <div className="text-right">
            <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter tabular-nums" data-testid="revenue-orders">{rev.orders}</div>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Orders fulfilled</span>
          </div>
        </div>

        <div className="relative px-8 md:px-14 pb-12 md:pb-14 -mt-2">
          <div className="flex items-end justify-between mb-3">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">This month's goal</span>
            <span className="font-mono text-xs tracking-[0.15em] uppercase text-foreground" data-testid="revenue-goal-label">
              ${month.toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-muted-foreground">/ ${goal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </span>
          </div>
          <div className="h-2.5 w-full bg-background border border-border overflow-hidden" data-testid="revenue-goal-bar">
            <motion.div className="h-full bg-volt" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} />
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-volt mt-2">{pct.toFixed(0)}% of monthly goal</div>
        </div>
      </div>
    </section>
  );
};
